import { DateInput } from '@mantine/dates';
import { useActiveForm } from 'pages/claims/components';
import React, { useEffect, useState } from 'react';
import { useCurrentSiteStore } from 'store';
import { nowISODate, toISODate } from 'utils/dateUtils';
import { getValueByPath } from 'utils/getValueByPath';

interface ModalDateInputProps {
  label: string;
  accessorKey: string;
  width?: number | string;
  required?: boolean;
  disabled?: boolean;
}

export const ModalDateInput: React.FC<ModalDateInputProps> = React.memo(
  ({
    label,
    accessorKey,
    width = '100%',
    required = false,
    disabled = false,
  }) => {
    const { timezone } = useCurrentSiteStore();

    const { form, prefix } = useActiveForm();

    const path = prefix ? `${prefix}.${accessorKey}` : accessorKey;
    const values = form.getValues();

    const value = getValueByPath(values, path);
    const error = getValueByPath(form?.errors, path);
    const [dateValue, setDateValue] = useState<string>(nowISODate(timezone));

    useEffect(() => {
      setDateValue(!value ? nowISODate(timezone) : value);
    }, [timezone, value]);

    const setDateValues = (newValue: string) => {
      setDateValue(newValue);
      form.setFieldValue(path, newValue);
    };

    return (
      <DateInput
        key={path}
        label={label}
        value={dateValue}
        onChange={(value) => {
          if (disabled) return;
          setDateValues(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const raw = (e.currentTarget as HTMLInputElement).value.trim();
            const date = toISODate(raw, { zone: timezone });
            if (date) {
              setDateValues(date);
              form.clearFieldError(path);
              (e.currentTarget as HTMLInputElement).blur();
            } else {
              form.setFieldError(path, 'Invalid date format (YYYY-MM-DD)');
            }
          }
        }}
        error={error}
        onError={(event) => {
          const errorMessage = event.currentTarget.value;
          form.setFieldError(path, errorMessage);
        }}
        placeholder="YYYY-MM-DD"
        valueFormat="YYYY-MM-DD"
        withAsterisk={required}
        radius={'md'}
        w={width}
        disabled={disabled}
      />
    );
  }
);
